import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MembershipTierService } from '../../../core/api-services/membership-tier.service';
import { SubscriptionBillingService } from '../../../core/api-services/subscription-billing.service';
import { formatAnnualPrice } from '../../../core/utils/subscription-billing-utils';

/**
 * Upgrade to Pro (Task Group 8)
 *
 * Route: /billing/upgrade. Core-only — a Pro org lands on Manage
 * Subscription instead (FR-4.3). No route guard for this redirect
 * (MembershipTierGuard protects Pro-*required* routes; this is the
 * inverse "Core-only" case, per tasks.md 8.5) — handled here in
 * `ngOnInit` after awaiting the tier fetch, so a fast page load can't
 * flash the comparison table at a Pro org before redirecting away.
 *
 * Reference: agent-os/specs/2026-06-29-core-pro-membership/spec.md FR-4
 */
@Component({
  selector: 'app-upgrade-to-pro',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="container py-5 mw-900px" *ngIf="ready()">
      <div class="text-center mb-5">
        <h2 class="mb-2">Upgrade to Pro</h2>
        <p class="text-muted">Unlock Agremo Map Analysis and the full Support section — org-wide, no seat limits.</p>
      </div>

      <div class="row g-4 mb-4">
        <div class="col-md-6">
          <div class="card h-100">
            <div class="card-body p-4">
              <h5 class="mb-3">Core</h5>
              <p class="text-muted small mb-4">Your current plan — $0</p>
              <ul class="list-unstyled small">
                <li class="mb-2"><i class="bi bi-dash-circle text-muted me-2"></i>Agremo: preview page only</li>
                <li class="mb-2"><i class="bi bi-dash-circle text-muted me-2"></i>Support: Resources links only (UPass, FieldWatch)</li>
                <li class="mb-2"><i class="bi bi-x-circle text-muted me-2"></i>No Support Tickets / Service Appointments</li>
                <li class="mb-2"><i class="bi bi-x-circle text-muted me-2"></i>No Training / Drone Videos</li>
              </ul>
            </div>
          </div>
        </div>

        <div class="col-md-6">
          <div class="card h-100 border-primary shadow-sm">
            <div class="card-body p-4">
              <h5 class="mb-3">Pro <span class="badge bg-primary ms-1">Recommended</span></h5>
              <p class="text-muted small mb-4">{{ priceLabel() }}, entire organization</p>
              <ul class="list-unstyled small">
                <li class="mb-2"><i class="bi bi-check-circle text-primary me-2"></i>Agremo: full access — request, run, and view analyses</li>
                <li class="mb-2"><i class="bi bi-check-circle text-primary me-2"></i>Full Support section</li>
                <li class="mb-2"><i class="bi bi-check-circle text-primary me-2"></i>Support Tickets + Service Appointments</li>
                <li class="mb-2"><i class="bi bi-check-circle text-primary me-2"></i>Training / Drone Videos</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div class="text-center">
        <button
          class="btn btn-primary btn-lg px-5"
          [disabled]="submitting()"
          (click)="upgrade()"
        >
          <span *ngIf="submitting()" class="spinner-border spinner-border-sm me-2" role="status">
            <span class="visually-hidden">Redirecting...</span>
          </span>
          {{ submitting() ? 'Redirecting to checkout…' : 'Upgrade to Pro — ' + priceLabel() }}
        </button>
        <p *ngIf="errorMessage()" class="text-danger small mt-3 mb-0">{{ errorMessage() }}</p>
        <p class="text-muted small mt-3 mb-0">Billed annually via Stripe. Manage or cancel anytime from Manage Subscription.</p>
      </div>
    </div>
  `,
})
export class UpgradeToProComponent implements OnInit {
  private router = inject(Router);
  private membershipTierService = inject(MembershipTierService);
  private billingService = inject(SubscriptionBillingService);

  ready = signal(false);
  submitting = signal(false);
  errorMessage = signal('');

  priceLabel = signal('Price unavailable');

  async ngOnInit(): Promise<void> {
    await this.membershipTierService.awaitReady();

    if (this.membershipTierService.isPro()) {
      this.router.navigate(['/billing']);
      return;
    }

    this.priceLabel.set(formatAnnualPrice(this.membershipTierService.priceCents()));
    this.ready.set(true);
  }

  async upgrade(): Promise<void> {
    this.submitting.set(true);
    this.errorMessage.set('');
    try {
      const session = await this.billingService.createCheckoutSession();
      this.billingService.redirect(session.url);
    } catch (e: unknown) {
      this.submitting.set(false);
      // A hub ApiError, or the redirect guard's Error; both carry a displayable message.
      this.errorMessage.set(e instanceof Error && e.message ? e.message : 'Could not start checkout. Please try again.');
    }
  }
}
